<template>
  <div class="suggestion-details-header">
    <div
      class="suggestion-details-header__section"
      :class="{ 'suggestion-details-header__section__automatic': automaticAction.sendChannel === channel.NON_OFFICIAL }"
    >
      <div class="suggestion-details-header__form-field">
        <label
          v-tooltip.right="{
            theme: 'error',
            html: true,
            autoHide: false,
            distance: 8,
            content: $t('campaigns.required-field'),
            shown: !suggestionDetail.name && !loadingSuggestionsDetail && tooltipEmptyError.name,
          }"
          >{{ $t('360.campaigns.details.name') }} <span class="required">*</span></label
        >
        <el-tooltip
          :visible="hasSpecialCharactersName"
          :raw-content="true"
          :content="$t('audiences.error-characters-name')"
          placement="right"
          popper-class="el--tooltip-error suggestion-details-header__input--tooltip"
        >
          <input
            class="suggestion-details-header__input"
            type="text"
            name="name"
            v-model="suggestionDetail.name"
            @input="handleNameInput"
            :class="{
              'suggestion-details-header__input--error': hasSpecialCharactersName,
            }"
          />
        </el-tooltip>
      </div>

      <div v-if="automaticAction.sendChannel === channel.NON_OFFICIAL">
        <label
          v-tooltip.right="{
            theme: 'error',
            html: true,
            autoHide: false,
            distance: 8,
            content: $t('campaigns.required-field'),
            shown: !automaticAction.audienceId && !loadingSuggestionsDetail && tooltipEmptyError.audience,
          }"
          >{{ $tc('_audience', 2) }} <span class="required">*</span></label
        >
        <v-select
          label="name"
          :clearable="false"
          v-model="audienceName"
          :options="audienceList"
          :placeholder="$tc('_select', 2)"
          @search="searchAudience"
        >
          <template #open-indicator="{ attributes }">
            <span v-bind="attributes" class="arrow-down"></span>
          </template>
          <template #no-options>
            {{ $t('no-results') }}
          </template>
        </v-select>
        <small
          class="suggestion-details-header__section__text-audience"
          v-if="audienceName"
          v-html="$tc('360.campaigns.details.message-audience', { count: formatAudienceCount(audienceCount) })"
        >
        </small>
      </div>

      <div
        v-show="getHasCampaignApproval && automaticAction.sendChannel === channel.SELLER"
        class="suggestion-details-header__form-field"
      >
        <el-checkbox
          class="suggestion-details-header__approval-checkbox"
          v-model="requiresApproval"
          :label="$t('360.campaigns.details.label-approval')"
          @change="handleStoreApprovalCheckbox"
        />
      </div>
    </div>

    <transition>
      <div v-show="showApprovalSection" class="suggestion-details-header__form">
        <div class="suggestion-details-header__form-field">
          <label>{{ $t('360.campaigns.details.text-area-label') }}</label>

          <textarea
            v-model="suggestionDetail.description"
            name="message"
            cols="30"
            rows="3"
            :placeholder="$tc('360.campaigns.details.placeholder')"
          />
        </div>

        <div class="suggestion-details-header__approval-section">
          <div class="suggestion-details-header__criteria">
            <label>{{ $t('360.campaigns.details.criteria') }}</label>

            <el-select-v2
              v-model="selectedCriteria"
              :options="criteriaOptions"
              style="width: 170px"
              class="grouping-select"
              popper-class="grouping-select-dropdown"
              @visible-change="handleDropdownVisibleState"
            >
              <template #default="{ item }">
                <span>{{ item.label }}</span>
              </template>

              <template #empty>
                <span>{{ $t('360.campaigns.details.no-match') }}</span>
              </template>
            </el-select-v2>
          </div>

          <div class="suggestion-details-header__criteria">
            <label
              v-tooltip.right="{
                theme: 'error',
                html: true,
                autoHide: false,
                distance: 8,
                content: $t('360.campaigns.details.tooltip.stores-approval'),
                shown:
                  automaticAction.sendChannel !== channel.NON_OFFICIAL &&
                  !suggestionDetail.stores &&
                  !loadingSuggestionsDetail &&
                  tooltipEmptyError.stores,
              }"
              >{{ $t('360.campaigns.details.stores-approval') }}</label
            >

            <el-cascader
              v-model="selectedStoresList"
              :options="cascaderFormattedOptions"
              :props="storeSelectionConfig"
              :placeholder="$t('360.campaigns.details.search')"
              :filter-method="cascaderFilter"
              filterable
              collapse-tags
              collapse-tags-tooltip
              :max-collapse-tags="2"
              class="grouping-select"
              popper-class="grouping-select-dropdown"
              @change="handleSuggestionStores"
              @visible-change="handleDropdownVisibleState"
            >
              <template #empty>
                <span>{{ $t('360.campaigns.details.no-match') }}</span>
              </template>
            </el-cascader>
          </div>

          <div class="suggestion-details-header__note">
            <p class="suggestion-details-header__note-text">
              <strong class="suggestion-details-header__note-asterisk">*</strong>
              {{ $t('360.campaigns.details.note.text') }}

              <strong class="suggestion-details-header__note-highlight">
                {{ $t('360.campaigns.details.note.span') }}
              </strong>

              {{ $t('360.campaigns.details.note.text-2') }}
              {{ $t('360.campaigns.details.note.text-3') }}
            </p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// libs
import { mapState, mapActions, mapGetters } from 'vuex';
import { getStoresForActionsPage } from '@/services/stores';
import { hasInvalidSpecialCharacters } from '@/utilities/validators/verifySpecialCharacters';
import { getAudiences } from '@/services/actions';
import { debounce } from 'lodash';
import vSelect from 'vue-select';
import { formatNumber } from '@/utilities/formatters';
import { channel } from '@/utilities/constants';

const CRITERIA = {
  CLUSTER: 'cluster',
  REGION: 'region',
};

const MODE = {
  EDIT: 'edit',
  CREATE: 'create',
};

export default {
  name: 'CampaignsManagementDetailsHeader',
  components: {
    vSelect,
  },
  data() {
    return {
      selectedCriteria: CRITERIA.CLUSTER,
      criteriaOptions: [
        { value: CRITERIA.CLUSTER, label: 'Cluster' },
        { value: CRITERIA.REGION, label: 'Região' },
      ],
      audienceList: [],
      selectedStoresList: [],
      storeSelectionConfig: { multiple: true, value: 'id' },
      storeHierarchyData: [],
      hasSpecialCharacters: false,
      channel,
    };
  },
  computed: {
    ...mapState('campaignsManagement', [
      'hasSpecialCharactersName',
      'suggestionDetail',
      'mode',
      'loadingSuggestionsDetail',
      'tooltipEmptyError',
      'automaticAction',
    ]),
    ...mapGetters(['getHasCampaignApproval']),
    ...mapGetters('campaignsManagement', ['getStoresId']),

    showApprovalSection() {
      return (
        this.suggestionDetail.isEnabledApproval &&
        this.getHasCampaignApproval &&
        this.automaticAction.sendChannel === channel.SELLER
      );
    },

    requiresApproval() {
      return !this.suggestionDetail.enabledApproval;
    },

    audienceName: {
      get() {
        return this.suggestionDetail?.campaigns?.[0]?.audience?.name;
      },
      set(newValue) {
        this.actionSaveAudience(newValue);
      },
    },

    audienceCount() {
      return this.suggestionDetail?.campaigns?.[0]?.audience?.count;
    },

    cascaderFormattedOptions() {
      const clusterOptions = this.storeHierarchyData[0]?.children || [];
      const regionOptions = this.storeHierarchyData[1]?.children || [];

      return this.selectedCriteria === CRITERIA.CLUSTER ? clusterOptions : regionOptions;
    },
    isEditMode() {
      return this.mode === MODE.EDIT;
    },

    isCreateMode() {
      return this.mode === MODE.CREATE;
    },
  },
  mounted() {
    this.getStores();
    this.fetchAudiences();

    if (this.isCreateMode) {
      this.setStatusTooltip({ key: 'name', value: false });
      this.setStatusTooltip({ key: 'audience', value: false });
    }
  },
  updated() {
    this.applyStoreUpdatesForEditMode();

    if (this.isCreateMode) {
      const stores = this.suggestionDetail.isEnabledApproval ? '' : [];
      this.updateSuggestionStores(stores);
    }
  },
  watch: {
    getStoresId() {
      if (this.getStoresId) this.selectedStoresList = [...this.getStoresId];
    },
  },
  methods: {
    ...mapActions('campaignsManagement', [
      'setErrorName',
      'setEnabledApproval',
      'updateSuggestionStores',
      'setStatusTooltip',
      'setActionName',
      'actionSaveAudience',
    ]),

    formatAudienceCount(totalcount) {
      return formatNumber(totalcount);
    },

    handleNameInput(event) {
      const value = event.target.value;

      this.verifySpecialCharacters(value);
      this.addActionName(value);
    },

    addActionName() {
      this.setActionName(this.suggestionDetail.name);
    },

    fetchAudiences() {
      getAudiences().then(({ data }) => {
        this.audienceList = data?.data ?? [];
      });
    },

    searchAudience(term) {
      if (!term) return;
      this.triggerDebounce(term);
    },

    triggerDebounce: debounce(function (term) {
      getAudiences({ term }).then(({ data }) => {
        this.audienceList = data?.data;
      });
    }, 500),

    verifySpecialCharacters(name) {
      const hasError = hasInvalidSpecialCharacters(name);
      this.setErrorName(hasError);
    },

    getStores() {
      getStoresForActionsPage().then(storeList => {
        this.storeHierarchyData = storeList.data.data || [];
      });
    },

    handleDropdownVisibleState(isVisible) {
      this.$emit('visible-change', isVisible);
    },

    handleSuggestionStores(selectedStores) {
      if (!selectedStores || !selectedStores.length) {
        this.updateSuggestionStores('');
        return;
      }

      const stores =
        this.selectedCriteria === CRITERIA.CLUSTER
          ? selectedStores.map(selected => selected[1]).join(',')
          : selectedStores.map(selected => selected[2]).join(',');

      this.updateSuggestionStores(stores);
    },

    handleStoreApprovalCheckbox() {
      this.setEnabledApproval();

      if (!this.suggestionDetail.isEnabledApproval) {
        // Update with empty array to avoid trigger tooltip error message
        this.updateSuggestionStores([]);
        this.selectedStoresList = [];
      }
    },

    cascaderFilter(node, keyword) {
      return node.label.toLowerCase().includes(keyword.toLowerCase());
    },

    applyStoreUpdatesForEditMode() {
      if (!this.suggestionDetail.stores) return;

      const isEditModeWithNoStoresSelected =
        this.isEditMode && this.suggestionDetail.isEnabledApproval && !this.selectedStoresList.length;

      if (isEditModeWithNoStoresSelected) {
        // Update with empty string to trigger tooltip error message
        // in case the user try to save with isEnabledApproval, but no selected stores
        this.updateSuggestionStores('');
      }
    },
  },
};
</script>

<style lang="scss">
.suggestion-details-header {
  &__section {
    display: grid;
    align-items: end;
    gap: 125px;
    grid-template-columns: 1fr 2fr;
    margin-bottom: 20px;

    &__text-audience {
      display: block;
      width: 315px;
      margin-top: 5px;
      font-family: Montserrat;
      font-size: 11px;
      color: #666666;
    }

    &__automatic {
      gap: 0px;
      align-items: baseline;

      .v-select {
        width: 330px;
      }
    }
  }

  &__form {
    display: grid;
    gap: 125px;
    grid-template-columns: 1fr 2fr;
    margin-bottom: 20px;
  }

  &__form-field {
    input,
    textarea {
      width: 305px;
    }

    textarea {
      height: 178px;
    }
  }

  &__input {
    &--tooltip {
      max-width: 270px !important;
    }

    &--error {
      @include error-inputs;
    }
  }

  &__approval-section {
    width: 300px;
  }

  &__criteria {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 16px;
  }

  &__note-asterisk {
    color: #ec3942;
    margin-right: 5px;
  }
}

.v-enter-active,
.v-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.v-enter-to,
.v-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>

<style lang="scss">
.grouping-select {
  .el-select {
    &__wrapper {
      border-radius: 25px;
      border: 1px solid $gray-500;
      box-shadow: none !important;

      .is-focused {
        border-color: $oto-omni !important;
      }
    }

    &__selection {
      font-size: 12px;
      font-weight: 600;
      color: $gray-800;
    }
  }

  .el-input__wrapper {
    border: 1px solid $gray-500;
    box-shadow: none !important;
  }

  .el-cascader__search-input {
    border: none;
    border-radius: 0;
    border-bottom: 1px solid $gray-500;

    &:focus {
      border: none;
      border-bottom: 1px solid $gray-500;
    }
  }
}

.grouping-select-dropdown {
  .el-cascader-panel {
    font-size: 12px;
    font-weight: 500;
    color: $gray-800;
  }
}

.suggestion-details-header__form-field {
  label.el-checkbox {
    span.el-checkbox__label {
      color: $gray-800;
      font-size: 12px;
      font-weight: 600;
      line-height: 20px;
    }
  }
}
</style>
